import './TransactionTable.css';
import React, { useState } from 'react';
import type {TransactionDetails, Aggregate} from '../types/StockhornTypes';
import {
  Table, TableBody, TableCell, TableHead, TableRow,
  Checkbox, Button, TextField, Box, Typography,
  FormControlLabel, Paper,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { parse, isWithinInterval, startOfDay, endOfDay } from 'date-fns';
import {CopyToClipboardModal, SelectFieldToFilterByModal, SelectStoredEntriesModal} from './Modals';
import FileSelector  from './FileSelector';
import {Cookies}  from '../js/localStorageCode';
import SkeletonTable from './loaders/SkeletonTable'
import { getAmountValue, sortTransactions, type SortDirection } from './sortUtils';
const PARTIAL_EXPENSE_RATIO = 0.15;
const environment = document.location.hostname.match(/meekmeek/) ? 'PROD' : document.location.hostname.match(/vercel/) ? 'PREVIEW': 'DEV';

export function TransactionTable() {
  const isLocal = process.env.REACT_APP_ENV === 'development';

  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [isDescending, setIsDescending] = useState<boolean>(false);
  const [columnFiltering, setColumnFiltering] = useState<boolean>(false);
  const [selectRows, setSelectRows] = useState<boolean>(true);
  const [selectedRowsIndex, setSelectedRowsIndex] = useState<number[]>([]);
  const [columnFilterValue, setColumnFilterValue] = useState<string>('');
  const [selectAllRows, setSelectAllRows] = useState<boolean>(false);
  const [selectedFilter, setSelectedFilter] = useState<string>('');
  const [sortConfig, setSortConfig] = useState<{field: keyof TransactionDetails; direction: SortDirection} | null>(null);
  const [filterByColumn, setFilterByColumn] = useState<boolean>(false);
  const [selectedColumnOption, setSelectedColumnOption] = useState<string>('Description');
  const [storedData, setStoredData] = useState<Record<string, any>>({});
  const [openCopyToClipboardModal, setOpenCopyToClipboardModal] = useState<boolean>(false);
  const [savedText, setSavedText] = useState<string>('');
  const [filesData, setFilesData] = useState<TransactionDetails[]>([]);
  const [fileName, setFileName] = useState<string>('');
  const [selectStoredItems, setSelectStoredItems] = useState<boolean>(false);
  const [storedOptions, setStoredOptions] = useState<string[]>([]);
  const [storedFilesData, setStoredFilesData] = useState<TransactionDetails[]>([]);
  const [isLoadingData, setIsLoadingData] = useState<boolean>(false);
  const [isNotMatch, setIsNotMatch] = useState<boolean>(false);


  document.title = environment +':Transaction Table in '+ (isLocal? 'Development':'Production');

  const cookies = new Cookies();

  // Callback function to receive file data from FileSelector
  const handleFilesSelected = (selectedFiles: TransactionDetails[]) => {
    setFilesData(selectedFiles);
    setStoredFilesData([]);
    setIsLoadingData(false);
  };


  const transactions:TransactionDetails[] =  storedFilesData.length === 0 && filesData.length? filesData : storedFilesData.length > 0? storedFilesData : [
  {
    "Date": "12/30/2024",
    "Description": "Stuff in TX",
    "CardMember": "JAIME A ROMERO",
    "AccountNumber": "-12016",
    "Amount": "12.34",
    "Category": "Merchandise & Supplies-Groceries",
    "ExtendedDetails": "Stuff",
    "AppearsOnYourStatementAs": "A place in            TX",
    "Address": "123 Main St",
    "CityState": "Anywhere\nTX",
    "ZipCode": "01234-5678",
    "Country": "UNITED STATES",
    "Reference": "'abcdefghijklmonpqrstuvwxyz'",
    "Type" : "Expense",
    //"Receipt": ""
  }];

  // All possible fields from the Transaction interface
  const originalFields = Object.keys(transactions[0]) as (keyof TransactionDetails)[];
  const allFields = !columnFiltering ? originalFields : originalFields.filter(field => !selectedFields.includes(field));

  // Handle field selection
  const handleFieldToggle = (field: string) => {
    setSelectedFields(prev =>
      prev.includes(field)
        ? prev.filter(f => f !== field)
        : [...prev, field]
    );
  };
    // Clear date filter
  const handleClearFilter = () => {
    setStartDate(null);
    setEndDate(null);
    setSelectedFilter('');
  };

  const handleToggleDateOrder = () => {
    const nextDirection: SortDirection = !sortConfig || sortConfig.field !== 'Date'
      ? 'asc'
      : sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ field: 'Date', direction: nextDirection });
    setSelectedFilter('Date');
    setIsDescending(nextDirection === 'desc');
    setSelectedRowsIndex([]);
  };

  const handleToggleAmountOrder = () => {
    const nextDirection: SortDirection = !sortConfig || sortConfig.field !== 'Amount'
      ? 'asc'
      : sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ field: 'Amount', direction: nextDirection });
    setSelectedFilter('Amount');
    setSelectedRowsIndex([]);
  }

  const handleToggleCategoryOrder = () => {
    const nextDirection: SortDirection = !sortConfig || sortConfig.field !== 'Category'
      ? 'asc'
      : sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ field: 'Category', direction: nextDirection });
    setSelectedFilter('Category');
    setSelectedRowsIndex([]);
  }

  const showCellContents = (field: string) => {
    setOpenCopyToClipboardModal(true);
    setSavedText(field);
  }

  const toggleRowSelection = (e: React.MouseEvent<HTMLTableCellElement, MouseEvent>) => {
    console.log(e);
    const index = Number((e.target as HTMLElement).getAttribute('data-index'));
    selectedRowsIndex.includes(index) ? setSelectedRowsIndex(prev => prev.filter(i => i !== index)) : setSelectedRowsIndex(prev => [...prev, index]);
  }

  const toggleFields = ['Date', 'Amount', 'Category'];
  const toggleFunctions: Record<string, () => void> = {'Date': handleToggleDateOrder, 'Amount': handleToggleAmountOrder, 'Category': handleToggleCategoryOrder};

  const handleFilterClick = (field: keyof TransactionDetails) => {
    setSelectedRowsIndex([]);
    setSelectedFilter(String(field));

    const nextDirection: SortDirection = !sortConfig || sortConfig.field !== field
      ? 'asc'
      : sortConfig.direction === 'asc' ? 'desc' : 'asc';

    setSortConfig({ field, direction: nextDirection });
    if (field === 'Date') {
      setIsDescending(nextDirection === 'desc');
    }

    if (toggleFields.includes(String(field))) {
      toggleFunctions[String(field)]();
    }
  }

  const getFilteredTransactions = () => {
    const allData = cookies.getAll();
    const selectEntries:string[]= [];
    console.log('All stored items:', allData);
    Object.entries(allData).forEach(([key, data]) => {
      selectEntries.push(key);
    })
      console.log('All keys:', selectEntries);
      setStoredOptions(selectEntries);
      setStoredData(allData);
      setSelectStoredItems(true);
  }

  const storeFilteredTransactions = (data:TransactionDetails[]) => {
    const name = new Date().toISOString().split('.')[0]+'_Filter_on_'+(isNotMatch?'NOT':'')+'"'+selectedColumnOption+'='+columnFilterValue+'"_from-'+fileName;
    const finalData = selectedRowsIndex.length>0 ? data.filter((dta,index)=>selectedRowsIndex.includes(index)) : data;
    cookies.setWithReducer(name, finalData, {columnFiltered:selectedColumnOption, filterUsed:columnFilterValue}, {expires: 365});
  }

  const filteredStringOrRegxp = (filterValue:string, itemValue:string, isDontMatch:boolean) =>{
      if (!filterValue || itemValue === undefined) return true;
      try {
        // Check if input is a regex (e.g., "/pattern/" or "pattern")
        const isRegex = filterValue.startsWith('/') && filterValue.endsWith('/');
        const pattern = isRegex ? filterValue.slice(1, -1) : filterValue;
        const regex = isRegex ? new RegExp(pattern, 'i') : new RegExp(pattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i');
        // Convert item value to string for matching
        const stringValue = itemValue.toString();
        return isDontMatch ? !regex.test(stringValue) : regex.test(stringValue);
      } catch (error) {
        console.warn(`Invalid regex for:`, filterValue);
        return true; // Ignore invalid regex, show all rows
      }
    }

    // Filter transactions by date range
  const filteredTransactions = transactions.filter(transaction => {
    if (!startDate && !endDate) return true;
    const transactionDate = parse(transaction.Date, 'MM/dd/yyyy', new Date());
    if (startDate && endDate) {
      return isWithinInterval(transactionDate, {
        start: startOfDay(startDate),
        end: endOfDay(endDate),
      });
    }
    if (startDate) {
      return transactionDate >= startOfDay(startDate);
    }
    if (endDate) {
      return transactionDate <= endOfDay(endDate);
    }
    return true;
  });

  const sortField = (sortConfig?.field ?? (selectedFilter || 'Date')) as keyof TransactionDetails;
  const sortDirection = sortConfig?.direction ?? (isDescending ? 'desc' : 'asc');

  let displayedTransactions = sortTransactions(filteredTransactions, sortField, sortDirection);

  if (columnFilterValue && selectedColumnOption) {
    displayedTransactions = displayedTransactions.filter(x => {
      const value = x[selectedColumnOption as keyof TransactionDetails];
      return value ? filteredStringOrRegxp(columnFilterValue, value, isNotMatch) : false;
    });
  }

  const AllRows = () => {
    if(columnFilterValue && selectAllRows){
      setSelectAllRows(prev => !prev)
    } else {
      setSelectAllRows(prev => !prev)
      setSelectedRowsIndex([]);
    }
  }

  const HeaderRowEntries = ({key, field}: {key: keyof TransactionDetails; field: keyof TransactionDetails}) => {
    const isActiveSort = sortConfig?.field === field || (!sortConfig && selectedFilter === String(field));
    const isDescendingSort = sortConfig?.field === field ? sortConfig.direction === 'desc' : isDescending && field === 'Date';
    const sortIndicator = isActiveSort ? (isDescendingSort ? downArrow : upArrow) : '';

    return (
      <TableCell
        key={key}
        onClick={() => handleFilterClick(field)}
        style={{ cursor: 'pointer', fontWeight: isActiveSort ? 'bold' : 'normal' }}
      >
        {field}{sortIndicator}
      </TableCell>
    );
  }

  const TableRowEntries = ({field, text, index}: {field: keyof TransactionDetails; text: string|undefined, index:number}) => {
    if(selectRows){
      return (
        <TableCell key={field} data-index={index}
            onClick={(e) => toggleRowSelection(e)}
            style={{cursor: 'pointer'}}
            title={text}
            >
          {text}
        </TableCell>

      )
    } else {
      return (
        <TableCell key={field}
            onClick={() => showCellContents(text? text : '')}
            style={field === selectedFilter ? {cursor: 'pointer', background:'#ffc', fontWeight: 'bold'}:{cursor: 'pointer'}}
            title={text}
          >
            {text}
          </TableCell>
      )
    }
  }

  const sumSelectedFields = (arrayOfObjects:TransactionDetails[], selectedIndexes:number[]) =>{
      return selectAllRows ? 
        arrayOfObjects.reduce((sum, obj) => sum + Number(getAmountValue(obj as object).toFixed(2)||0),0)
      : selectedIndexes.reduce((sum, index) => sum + Number(getAmountValue(arrayOfObjects[index] as object).toFixed(2)||0),0);
  }

  const upArrow = String.fromCharCode(8657);
  const downArrow = String.fromCharCode(8659);
  const totalAmount = sumSelectedFields(displayedTransactions,selectedRowsIndex).toFixed(2);

  filesData.length? console.log(filesData) : void(0);

  console.log('selectedColumnOption:', selectedColumnOption);
  const hasType = Boolean(displayedTransactions[0] && displayedTransactions[0].Type);
  const aggregateExpenses:Aggregate = {
    expenses:{amount:0, count:0},
    cashDistibutions:{amount:0, count:0},
    partialExpenes:{
      amount:{
        cashDistibutions:0,
        expenses:0,
      },
      count:0
    }
  }
  if(hasType){
    displayedTransactions.forEach(row=>{
      if(row.Type==="Expense"){
        aggregateExpenses.expenses.amount += getAmountValue(row as object);
        aggregateExpenses.expenses.count += 1;
      }
      else if(row.Type==="PartialExpense"){
        aggregateExpenses.partialExpenes.amount.expenses += (getAmountValue(row as object)*PARTIAL_EXPENSE_RATIO);
        aggregateExpenses.partialExpenes.amount.cashDistibutions += (getAmountValue(row as object)*(1-PARTIAL_EXPENSE_RATIO));
        aggregateExpenses.partialExpenes.count += 1;
      } else if(row.Type==="NonExpense"){
        aggregateExpenses.cashDistibutions.amount += getAmountValue(row as object);
        aggregateExpenses.cashDistibutions.count += 1;
      }
    })

  }

 return (
    <>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Box className="MyTable" p={3}>
          <Typography variant="h4" align='center' gutterBottom>
            Transaction Table
          </Typography>
            {/* Date Filter and Sort Controls */}
            <Paper elevation={0} sx={{ mb: 0, p: 2 }} className="filterWidth">
                          {transactions.length>1 &&(
              <Typography variant="h6">Filter and Sort</Typography>
                          )}
              <Box display="flex" gap={2} mb={2} justifyContent={"center"} flexWrap="wrap">
                {transactions.length>1 &&(
                  <>
                <DatePicker
                  className="myDates"
                  label="Start Date"
                  value={startDate}
                  onChange={(newValue) => setStartDate(newValue)}
                  slotProps={{ textField: { size: 'small', variant: 'outlined' } }}
                  format="MM/dd/yyyy"
                />
                <DatePicker
                  className="myDates"
                  label="End Date"
                  value={endDate}
                  onChange={(newValue) => setEndDate(newValue)}
                  slotProps={{ textField: { size: 'small', variant: 'outlined' } }}
                  format="MM/dd/yyyy"
                />
                <Button
                  className="button"
                  variant="outlined"
                  color="secondary"
                  onClick={handleClearFilter}
                >
                  Clear
                </Button>
                <span className ="containerSpan">
                <TextField
                  className="myDescription"
                  label={`Filter by ${selectedColumnOption}`}
                  value={columnFilterValue}
                  onChange={(e) =>{
                    setColumnFilterValue(e.target.value);
                    if(!e.target.value){
                      setSelectAllRows(false);
                      setSelectedRowsIndex([]);
                    }}}
                  variant="outlined"
                  size="small"
                />
                {columnFilterValue && (
                  <div  style={{whiteSpace:"nowrap"}}>
                  <Button
                    className="buttonInside"
                    variant="outlined"
                    color="primary"
                    onClick={AllRows}
                  >
                    {!selectAllRows ? 'Show Total' : 'Clear Total'}
                  </Button>
                <Checkbox
                      checked={isNotMatch}
                      onChange={() => setIsNotMatch(prev => !prev)}
                    />NOT</div>
                )}
                </span>
                  </>
                )}
              {transactions.length>1 &&(
                <Button
                  className="button"
                  variant="outlined"
                  color="primary"
                  onClick={storeFilteredTransactions.bind(null, displayedTransactions)}
                >
                  Save Transactions
                </Button>
              )}
                <Button
                  className="button"
                  variant="outlined"
                  color="primary"
                  onClick={getFilteredTransactions}
                >
                  Get Transactions
                </Button>
                {(selectAllRows || (selectRows && !!selectedRowsIndex.length)) && transactions.length>1 && (
                <TextField
                  className="myAmount"
                  label="Total Amount"
                  value={totalAmount}
                  variant="outlined"
                  size="small"
                  disabled={true}
                />
                )}
              {transactions.length>1 && (
                <Button
                  className="button"
                  variant="outlined"
                  color="primary"
                  onClick={()=>setFilterByColumn(prev => !prev)}
                >
                  {filterByColumn ? 'Filter by Description' : 'Pick Filter Column'}
                </Button>
              )}
                {!columnFilterValue && transactions.length>1 &&(
                  <Button
                    className="button"
                    variant="outlined"
                    color="primary"
                    onClick={handleToggleDateOrder}
                  >
                    {isDescending ? 'Sort Oldest First' : 'Sort Newest First'}
                  </Button>
                )}
                {transactions.length>1 &&(
                  <>
                <Button
                  className="button"
                  variant="outlined"
                  color="primary"
                  onClick={()=>setColumnFiltering(prev => !prev)}
                >
                  {!columnFiltering ? 'Hide Columns' : 'All Columns'}
                </Button>
                <Button
                  variant="outlined"
                  className="button"
                  color="primary"
                  onClick={()=>setSelectRows(prev => !prev)}
                >
                  {!selectRows ? 'Select Rows' : 'Copy Entry Text'}
                </Button>
                </>
                )}
                <FileSelector onFilesSelected={handleFilesSelected} setFileName={(val)=>{setFileName(val)}} onLoading={()=>{setIsLoadingData(true)}}/>
                  {hasType &&<span>{JSON.stringify(aggregateExpenses)}</span>}
            </Box>
            </Paper>
            {columnFiltering &&(
              <Paper elevation={0} sx={{ mb: 0, p: 0 }}>
                <Box mb={2}>
                  <Typography variant="subtitle1">Select Hidden Columns:</Typography>
                  {originalFields.map(field => (
                    <FormControlLabel
                      key={field}
                      control={
                        <Checkbox
                          checked={selectedFields.includes(field)}
                          onChange={() => handleFieldToggle(field)}
                        />
                      }
                      label={field}
                    />
                  ))}
                </Box>
              </Paper>
            )}
            {transactions.length <2 && (
              <Typography variant="h6" color="error" gutterBottom textAlign={"center"}>
                No transactions to display. Please upload a valid JSON file.
              </Typography>
            )}

            {isLoadingData  ? (
              <SkeletonTable rows={10} columns={allFields.length} />
            ) : (transactions.length <2 ? null :
            <Paper elevation={0} sx={{ mb: 4, p: 0 }}>
              <Typography variant="h6" textAlign={'center'}>Original Transactions from <span className={'fileNames'}>{fileName}</span>, we have {displayedTransactions.length} entries</Typography>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    {allFields.map(field => (
                      <HeaderRowEntries field={field} key={field} />
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayedTransactions.map((transaction, index) => (
                    <TableRow key={index} style={(selectRows && selectedRowsIndex.includes(index)) || selectAllRows? {background: "#ddd"}:{background: 'inherit'}}>
                      {allFields.map(field=>
                        <TableRowEntries text={transaction[field]} field={field} key={field} index={index}/>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
            )}
        </Box>
      </LocalizationProvider>
      <CopyToClipboardModal
        open={openCopyToClipboardModal}
        onClose={() => setOpenCopyToClipboardModal(false)}
        contents={savedText}
      />
      <SelectFieldToFilterByModal
        open={filterByColumn}
        onClose={() => setFilterByColumn(false)}
        selectedColumn={(val)=>{setSelectedColumnOption(val)}}
        originalFields={originalFields}
      />
      <SelectStoredEntriesModal
        open={selectStoredItems}
        storedData={storedData}
        onClose={() => setSelectStoredItems(false)}
        onSelect={(val)=>{console.log(val);setStoredFilesData(val)}}
        //onSelect={(val)=>{setStoredFilesData(val.value)}}
        options={storedOptions}
      />
    </>

  );
};


